/* eslint-disable ember/no-computed-properties-in-native-classes */
import Model, {
  attr,
  belongsTo,
  type AsyncBelongsTo,
} from '@warp-drive/legacy/model';
import { or, alias } from '@ember/object/computed';
import type { Type } from '@warp-drive/core/types/symbols';
import type { FragmentArray } from 'ember-data-model-fragments/array/fragment';
import { fragmentArray } from 'ember-data-model-fragments/attributes';
import type { EnumValue } from '@linc-technologies/linced-common/enums/base-enum';
import type GroupType from '@linc-technologies/linced-common/enums/group-type';
import type Member from '@linc-technologies/linced-common/models/groups/member';
import type School from '@linc-technologies/linced-common/models/school';

// Proto: groups.Group.
export default class Group extends Model {
  declare [Type]: 'group';

  // Attrs
  // createTime is when the resource was created in seconds from the epoch.
  @attr('number')
  declare createTime: number | undefined;

  // updateTime is the last time the resource was modified in seconds from
  // the epoch.
  @attr('number')
  declare updateTime: number | undefined;

  // externalId is the id of this resource in another system.
  @attr('string', { defaultValue: '' })
  declare externalId: string;

  // schoolId is the school the group is assigned to.
  @belongsTo('school', { async: true, inverse: null })
  declare schoolId: AsyncBelongsTo<School>;

  // campusId is the school campus the group is assigned to.
  @attr('string', { defaultValue: '' })
  declare campusId: string; // TODO: Add relationship when we implement campuses.

  // key is an optional migration identifier, for mapping between v3 and v4.
  @attr('string', { defaultValue: '' })
  declare key: string;

  // templateId is the unique identifier of the assessment template this
  // resource has been generated from.
  @attr('string', { defaultValue: '' })
  declare templateId: string; // TODO: Bind relationship to template ?

  // heroId is the unique identifier across the system for systems that
  // require an integer.
  @attr('number', { defaultValue: 0 })
  declare heroId: number;

  // Membership
  @fragmentArray('groups/member')
  declare learners: FragmentArray<Member>;

  @fragmentArray('groups/member')
  declare staff: FragmentArray<Member>;

  @fragmentArray('groups/member')
  declare caregivers: FragmentArray<Member>;

  // type specifies how the group is used for filtering purposes by other
  // services.
  @attr('number', { defaultValue: 0 })
  declare type: EnumValue<typeof GroupType> | 0;

  // autoGroupType specifies the type of calculation that will be performed
  // to manage the members within the group.
  @attr('number', { defaultValue: 0 })
  declare autoGroupType: number;

  // name is the group name, as known by linc-ed, the default.
  // For example, Class.
  @attr('string', { defaultValue: '' })
  declare name: string;

  // label is the display name of the group if edited by a School Admin.
  @attr('string', { defaultValue: '' })
  declare label: string;

  // year specifies the calendar year to which this Group relates.
  @attr('number', { defaultValue: 0 })
  declare year: number;

  // grouping is a heading under which a set of groups are categorised.
  @attr('string', { defaultValue: '' })
  declare grouping: string;

  // team is a department or other grouping of homerooms.
  @attr('string', { defaultValue: '' })
  declare team: string;

  // TODO: add params
  // filter provides a comma separated string of query params and values that
  // are used to generate the group.
  // It needs to be part of the model so that it won't be nuked by ember-data.
  @attr('string', { defaultValue: '' })
  declare filter: string;

  // status is used to track group status in SAIS synced schools. Not restricted to SAIS.
  @attr('number', { defaultValue: 0 })
  declare status: number;

  // RBAC.
  @attr('string', { defaultValue: '' })
  declare requiredRole: string;

  @attr('boolean', { defaultValue: false })
  declare membershipHidden: boolean;

  // isImported specifies if the request is coming from an import
  @attr('boolean', { defaultValue: false })
  declare isImported: boolean;

  // Custom/Computed Attrs
  // bannerText provides a fallback name for the group name, if a label doesn't
  // exist.
  @or('label', 'name')
  declare bannerText: unknown;

  @attr('string', { defaultValue: '' })
  declare profileMediaId: string;

  @alias('profileMediaId')
  declare profileMedia: string | undefined;

  @alias('profileMediaId')
  declare mediaId: string | undefined; // required by AvatarImage component
}
